import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './styles.css';
import Icon from '../../components/Icons';
import SEO from '../../components/SEO';

export default function About() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [visibleSections, setVisibleSections] = useState([]);
  const heroRef = useRef(null);
  const sectionRefs = useRef([]);

  const toggleFAQ = (index) => setOpenFAQ(openFAQ === index ? null : index);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);

      // Scroll reveal for sections
      sectionRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.8 && !visibleSections.includes(index)) {
            setVisibleSections(prev => [...prev, index]);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [visibleSections]);

  // Force reload on navigation to fix CSS animation issues
  useEffect(() => {
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    if (navigationEntry && navigationEntry.type === 'navigate') {
      // This was a client-side navigation, force reload to reset animations
      window.location.reload();
    }
  }, []);

  const faqs = [
    {
      question: "Why not just use Google Drive or iCloud?",
      answer: "Traditional cloud storage is designed for convenience, not continuity. EternaVaults was built for families — with private vaults for each member, inheritance protocols, and transparent audit trails. It's not where you store; it's where you preserve."
    },
    {
      question: "What happens if I pass away? Can my family still access everything?",
      answer: "Yes. Through EternaVaults' time-delayed emergency protocol, trusted custodians can gain access when needed most. Your family's documents, heirlooms, and stories remain reachable — without compromising privacy or intent."
    },
    {
      question: "Is my data really secure?",
      answer: "Absolutely. EternaVaults employs zero-knowledge encryption and AES-256 security — the same standard trusted by private banks. Even in a breach scenario, your data remains unreadable and exclusively yours."
    },
    {
      question: "How is this different from a regular photo album or drive?",
      answer: "EternaVaults is preservation, not storage. It holds a family's collective identity — memories, heirlooms, documents, and meaning — all structured for multi-generational access with lineage-aware permissions."
    },
    {
      question: "What if some family members aren't tech-savvy?",
      answer: "Our platform was designed for grace and simplicity. A single custodian can manage the vault on behalf of others, while summaries and guided access options keep everyone included without complexity."
    },
    {
      question: "What about compliance and data privacy?",
      answer: "We adhere to GDPR and SOC 2 Type II standards. Every vault's data lifecycle, residency, and encryption policy is transparently documented. Your vault, your data — always under your control."
    }
  ];

  const parallaxOffset = scrollY * 0.3;

  return (
    <>
      <SEO
        title="About | EternaVaults – Continuity & Governance Infrastructure"
        description="EternaVaults is an independent continuity and governance infrastructure for multi-generational and institutional families. Not affiliated with similarly named products."
        keywords="luxury digital vault, digital vault for family offices, high net worth digital vault, ultra high net worth digital vault, family office technology, private wealth digital vault, digital legacy, family vault, zero knowledge, inheritance, family office, digital estate, legacy management, family continuity, luxury legacy"
        canonical="https://eternavaults.com/about"
        ogImage="/about-og-image.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About EternaVaults",
          "description": "A private, encrypted family legacy vault for multi-generational continuity and digital heritage preservation.",
          "url": "https://eternavaults.com/about",
          "mainEntity": {
            "@type": "Organization",
            "name": "EternaVaults",
            "description": "Independent continuity and governance infrastructure for multi-generational and institutional families",
            "@id": "https://eternavaults.com/#organization",
            "url": "https://eternavaults.com",
            "sameAs": [
              "https://linkedin.com/company/eternavaults"
            ]
          }
        }}
        faqSchema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [{
            "@type": "Question",
            "name": "Is EternaVaults affiliated with other similarly named products?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. EternaVaults is an independent continuity and governance infrastructure and is not affiliated with similarly named products."
            }
          }]
        }}
      />

      <div className="about-page">
        {/* Ambient Particles Background */}
        <div className="particles-background">
          {[...Array(15)].map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${20 + Math.random() * 20}s`
            }} />
          ))}
        </div>

        {/* Vertical Gold Spine */}
        <div className="gold-spine"></div>

        {/* Hero Section */}
        <header
          className="about-hero"
          ref={heroRef}
          style={{ transform: `translateY(${parallaxOffset * 0.5}px)` }}
        >
          <div className="hero-vignette"></div>
          <div className="hero-content">
            <div className="hero-kicker">The Sovereign-Grade Digital Continuity Platform</div>
            <h1 className="hero-title">Governance, Continuity, and Legacy, Enforced by Software</h1>
            <p className="hero-subtitle">
              EternaVaults didn't reinvent the vault — it reinvented digital continuity for multi-entity families.
            </p>
            <div className="hero-divider"></div>
            <p className="hero-tagline">
              Continuity Domains™, automated succession, sovereign-grade rule enforcement, and true air-gapped resilience
              make it the first platform engineered for the real complexity of UHNW estates. This isn't storage.
              It's governance, continuity, and legacy, enforced by software.
            </p>
          </div>
        </header>

        <div className="about-content-wrapper">
          <main className="about-content">
            {/* Founder Quote */}
            <blockquote className="founder-quote">
              "Continuity is not convenience. It is care."
              <cite>— Auro Rekha</cite>
            </blockquote>
            {/* Purpose Section */}
            <section
              className={`about-section purpose-section ${visibleSections.includes(0) ? 'visible' : ''}`}
              ref={el => (sectionRefs.current[0] = el)}
            >
              <div className="section-divider"></div>
              <div className="section-header">
                <h2 className="section-title">The Reason We Exist</h2>
              </div>
              <div className="section-body">
                <p style={{
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: '#e2c89c',
                  marginBottom: '1.5rem',
                  lineHeight: '1.7'
                }}>
                  EternaVaults is an independent continuity and governance infrastructure for multi-generational and institutional families.
                </p>
                <p style={{
                  fontSize: '0.9rem',
                  fontStyle: 'italic',
                  color: 'rgba(226, 200, 156, 0.7)',
                  marginBottom: '1.5rem'
                }}>
                  EternaVaults is not affiliated with similarly named products.
                </p>
                <p>
                  Families today live across continents and clouds. Their most meaningful records — wills, memories, photographs, letters — are scattered across devices and forgotten logins.
                </p>
                <p>
                  EternaVaults was created to restore order and permanence — a single sanctuary where meaning endures, protected by design and guided by privacy.
                </p>
              </div>
            </section>

            {/* Principles Section */}
            <section
              className={`about-section principles-section ${visibleSections.includes(1) ? 'visible' : ''}`}
              ref={el => (sectionRefs.current[1] = el)}
            >
              <div className="section-divider"></div>
              <div className="section-header">
                <h2 className="section-title">Principles of Continuity</h2>
              </div>
              <div className="principles-grid">
                <div className="principle-card">
                  <div className="principle-icon">
                    <Icon name="lock" size={32} />
                  </div>
                  <h3 className="principle-title">Discretion First</h3>
                  <p className="principle-text">Silence is our default setting. Privacy is not a feature — it is the foundation.</p>
                </div>
                <div className="principle-card">
                  <div className="principle-icon">
                    <Icon name="shield" size={32} />
                  </div>
                  <h3 className="principle-title">Zero Knowledge, Infinite Trust</h3>
                  <p className="principle-text">Your data is encrypted before it ever touches our servers. Only your family holds the keys.</p>
                </div>
                <div className="principle-card">
                  <div className="principle-icon">
                    <Icon name="vault" size={32} />
                  </div>
                  <h3 className="principle-title">Structure Beyond Time</h3>
                  <p className="principle-text">Architecture built for generations — not product cycles. Designed to evolve, not expire.</p>
                </div>
                <div className="principle-card">
                  <div className="principle-icon">
                    <Icon name="users" size={32} />
                  </div>
                  <h3 className="principle-title">Human Stewardship</h3>
                  <p className="principle-text">Continuity guided by people, not tickets. Our partnerships are relationships, not logins.</p>
                </div>
                <div className="principle-card">
                  <div className="principle-icon">
                    <Icon name="gallery" size={32} />
                  </div>
                  <h3 className="principle-title">Transparency with Dignity</h3>
                  <p className="principle-text">Visibility without exposure. Every access is recorded, but never exploited.</p>
                </div>
                <div className="principle-card">
                  <div className="principle-icon">
                    <Icon name="family-tree" size={32} />
                  </div>
                  <h3 className="principle-title">Legacy in Motion</h3>
                  <p className="principle-text">Built to grow with your lineage — a living archive of memory, story, and identity.</p>
                </div>
              </div>
            </section>

            {/* Independence & Permanence Section */}
            <section
              className={`about-section independence-section ${visibleSections.includes(2) ? 'visible' : ''}`}
              ref={el => (sectionRefs.current[2] = el)}
            >
              <div className="section-divider"></div>
              <div className="section-header">
                <h2 className="section-title">Independence & Permanence</h2>
              </div>
              <div className="section-body independence-body">
                <p className="independence-statement">
                  EternaVaults is an independent, self-funded continuity infrastructure. We operate free from external investor pressure so the systems families rely on remain stable, durable, and governed solely by their intended purpose — not market cycles or exit incentives.
                </p>
                <div className="independence-grid">
                  <div className="independence-point">
                    <div className="independence-icon">
                      <Icon name="shield-check" size={24} />
                    </div>
                    <div>
                      <h4>No External Investors</h4>
                      <p>Self-funded and privately held, ensuring decisions prioritize continuity over quarterly returns.</p>
                    </div>
                  </div>
                  <div className="independence-point">
                    <div className="independence-icon">
                      <Icon name="infinity" size={24} />
                    </div>
                    <div>
                      <h4>Built for Generational Continuity</h4>
                      <p>Our mandate is singular: systems designed to outlast founders, advisors, and market cycles.</p>
                    </div>
                  </div>
                  <div className="independence-point">
                    <div className="independence-icon">
                      <Icon name="lock" size={24} />
                    </div>
                    <div>
                      <h4>No Acquisition Agenda</h4>
                      <p>Infrastructure cannot depend on someone else's timeline. We're built to last, not to exit.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Experience Section */}
            <section
              className={`about-section experience-section ${visibleSections.includes(3) ? 'visible' : ''}`}
              ref={el => (sectionRefs.current[3] = el)}
            >
              <div className="section-divider"></div>
              <div className="section-header">
                <h2 className="section-title">What Families Receive</h2>
              </div>
              <div className="experience-grid">
                <div className="experience-card">
                  <div className="experience-icon">
                    <Icon name="vault" size={40} />
                  </div>
                  <h3 className="experience-title">Private Family Vault</h3>
                  <p className="experience-text">An encrypted sanctuary designed to last beyond lifetimes.</p>
                </div>
                <div className="experience-card">
                  <div className="experience-icon">
                    <Icon name="family-tree" size={40} />
                  </div>
                  <h3 className="experience-title">Lineage Architecture</h3>
                  <p className="experience-text">Every member, story, and document bound within a generational structure.</p>
                </div>
                <div className="experience-card">
                  <div className="experience-icon">
                    <Icon name="emergency" size={40} />
                  </div>
                  <h3 className="experience-title">Emergency Protocols</h3>
                  <p className="experience-text">Secure, time-delayed access for those entrusted when life demands continuity.</p>
                </div>
                <div className="experience-card">
                  <div className="experience-icon">
                    <Icon name="bell" size={40} />
                  </div>
                  <h3 className="experience-title">Discreet Communication</h3>
                  <p className="experience-text">Quiet notifications and private coordination across your family network.</p>
                </div>
                <div className="experience-card">
                  <div className="experience-icon">
                    <Icon name="gallery" size={40} />
                  </div>
                  <h3 className="experience-title">Transparent Chronicle</h3>
                  <p className="experience-text">Immutable audit trail for peace of mind — clarity without intrusion.</p>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section
              className={`about-section faq-section ${visibleSections.includes(4) ? 'visible' : ''}`}
              ref={el => (sectionRefs.current[4] = el)}
            >
              <div className="section-divider"></div>
              <div className="section-header">
                <h2 className="section-title">Inquiries, Answered Discreetly</h2>
              </div>
              <div className="vault-qa-list">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`vault-qa-drawer ${openFAQ === index ? 'open' : ''}`}
                    onClick={() => toggleFAQ(index)}
                  >
                    <div className="qa-drawer-header">
                      <div className="qa-drawer-icon">
                        {openFAQ === index ? (
                          <Icon name="key" size={20} />
                        ) : (
                          <Icon name="lock" size={20} />
                        )}
                      </div>
                      <h3 className="qa-question">{faq.question}</h3>
                      <div className="qa-toggle">
                        <Icon name={openFAQ === index ? "arrow-up" : "arrow-down"} size={16} />
                      </div>
                    </div>
                    {openFAQ === index && (
                      <div className="qa-drawer-content">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <section
              className={`about-section cta-section-final ${visibleSections.includes(5) ? 'visible' : ''}`}
              ref={el => (sectionRefs.current[5] = el)}
            >
              <div className="section-divider"></div>
              <div className="section-header">
                <h2 className="section-title">Ready to Preserve What Time Cannot Touch?</h2>
              </div>
              <div className="cta-content">
                <p className="cta-intro">
                  Request a private consultation and experience the permanence of true continuity.
                </p>
                <div className="cta-actions">
                  <Link to="/signup" className="cta-button-primary">
                    Request Private Access →
                  </Link>
                  <Link to="/contact" className="cta-button-secondary">
                    Speak With a Legacy Advisor →
                  </Link>
                </div>
                <p className="cta-note">
                  EternaVaults is invitation-only — your inquiry remains fully confidential.
                </p>
                <p className="cta-note" style={{ marginTop: '1.5rem', fontSize: '15px' }}>
                  For family offices: <Link to="/family-office-digital-legacy" style={{ color: '#D9B45F', textDecoration: 'none', borderBottom: '1px solid rgba(217, 180, 95, 0.3)' }}>Explore the Family-Office Edition</Link> •
                  For legal & fiduciary institutions: <Link to="/institutional-digital-vault" style={{ color: '#D9B45F', textDecoration: 'none', borderBottom: '1px solid rgba(217, 180, 95, 0.3)' }}>Discover the Institutional Vault</Link>
                </p>
              </div>
            </section>

            {/* Signature Line */}
            <div className="signature-line">
              <p>— EternaVaults · Legacy Secured · Invitation Only</p>
              <p style={{
                fontSize: '0.75rem',
                opacity: 0.6,
                fontStyle: 'italic',
                marginTop: '1rem'
              }}>
                EternaVaults is an independent continuity and governance infrastructure. Not affiliated with similarly named products.
              </p>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
